import type { GrainSpec } from "./grain";
import type { ThemeId } from "../themes/types";

/**
 * The material each theme is shot on.
 *
 * These are the numbers that decide whether a theme reads as a film still or
 * as a web page with a filter on it, so they are described in film terms and
 * tuned by eye rather than derived from anything.
 */
export type TextureSpec = {
  grain: GrainSpec;
  /** Opacity of the grain layer. */
  grainOpacity: number;
  /**
   * Gate weave: how far the frame drifts in the projector gate, in px. Film
   * never sits perfectly still — the sprocket registration is mechanical, and
   * a fraction of a pixel of wander is most of why projected footage feels
   * alive and a div does not. Zero disables it.
   */
  weave: number;
  /** Seconds for one full weave cycle. Slower reads as a steadier projector. */
  weavePeriod: number;
};

export const TEXTURES: Record<ThemeId, TextureSpec> = {
  /*
   * Not film at all — phosphor. Fine, dense, and green, because what you are
   * looking at is a screen rather than a print of one.
   */
  matrix: {
    grain: {
      size: 160,
      density: 0.7,
      contrast: 0.5,
      clump: 0.25,
      tint: [0.55, 1, 0.6],
      frames: 6,
    },
    grainOpacity: 0.16,
    // A CRT has no gate. The image is stable; only the phosphor moves.
    weave: 0,
    weavePeriod: 0,
  },

  /*
   * 1979 anamorphic, pushed. Coarse cold crystals with real gaps between them,
   * and a gate that wanders — the Nostromo is a ship that rattles.
   */
  alien: {
    grain: {
      size: 192,
      density: 0.4,
      contrast: 0.75,
      clump: 0.45,
      tint: [0.8, 1, 1.05],
      frames: 8,
    },
    grainOpacity: 0.28,
    weave: 0.6,
    weavePeriod: 7,
  },

  /*
   * The coarsest of the four: heavy warm grain in the shadows, sparse enough
   * that the clumps are individually visible. Period horror stock, underlit.
   */
  conjuring: {
    grain: {
      size: 208,
      density: 0.32,
      contrast: 0.95,
      clump: 0.6,
      tint: [1.1, 0.92, 0.75],
      frames: 8,
    },
    grainOpacity: 0.34,
    weave: 0.9,
    weavePeriod: 5.5,
  },

  /*
   * Kubrick shot it clean and lit it bright, so the grain is fine and neutral.
   * The weave is the slowest here: the Overlook is oppressively steady.
   */
  shining: {
    grain: {
      size: 176,
      density: 0.5,
      contrast: 0.6,
      clump: 0.35,
      tint: [1, 0.98, 0.94],
      frames: 6,
    },
    grainOpacity: 0.22,
    weave: 0.45,
    weavePeriod: 9,
  },
};
